import React from 'react';
import Document from '@uber/base-icons/document';
import { useStyletron } from 'baseui';
import { isFrameClassificationLabel } from '../../../common/side-panel/utils/utils';
import type { AnnotationLabelT } from '../../../common/types/types';
import { TASK_QUESTIONNAIRE_ID, TASK_QUESTIONNAIRE_TITLE } from '../../side-panel/const';
import { LabelItem } from '../../side-panel/labels/label-item';
import { RequiredLabelBadge } from '../../side-panel/labels/required-label-badge';
import { getLabelListItemOverrides, getTagOverrides } from './label-list-overrides';
type TaskQuestionnaireComponentT = {
  Component: ({
    onClose
  }: {
    onClose?: () => void;
  }) => React.ReactNode;
  status: 'PENDING' | 'DONE' | 'OPTIONAL';
};
type PropsT = {
  labels: AnnotationLabelT[];
  annotationLabels: AnnotationLabelT[];
  selectedLabel?: AnnotationLabelT;
  annotationCountMap: Record<string, number>;
  onLabelClick: (name: string) => void;
  onCountClick: (name: string) => void;
  handleLabelColorChange: (payload: {
    color: string;
    title: string;
  }) => boolean;
  labelListItemEnhancer?: (labelTitle: string) => React.ReactNode;
  hideCountTagForLabel?: (labelTitle: string) => boolean;
  getRenderIcon: (label: AnnotationLabelT) => (props: {
    strokeColor: string;
  }) => React.ReactNode;
  taskQuestionnaireComponent?: TaskQuestionnaireComponentT;
  onOpenTaskQuestionnaire?: () => void;
  shouldShowTaskQuestionnaire?: boolean;
};
export const LabelList = ({
  labels,
  annotationLabels,
  selectedLabel,
  annotationCountMap,
  onLabelClick,
  onCountClick,
  handleLabelColorChange,
  labelListItemEnhancer,
  hideCountTagForLabel,
  getRenderIcon,
  taskQuestionnaireComponent,
  onOpenTaskQuestionnaire,
  shouldShowTaskQuestionnaire = false
}: PropsT) => {
  const [css, theme] = useStyletron();
  const objectLabels = labels.filter(label => !isFrameClassificationLabel(label));
  const nonObjectLabels = labels.filter(label => isFrameClassificationLabel(label));
  const shortcutKeyByTitle = new Map(annotationLabels.map((label, index) => [label.title, index < 9 ? index + 1 : undefined]));
  const taskQuestionnaireShortcutKey = annotationLabels.length < 9 ? annotationLabels.length + 1 : undefined;
  const renderLabelItem = (label: AnnotationLabelT, isLast = false) => {
    const isSelected = label.title === selectedLabel?.title;
    return <LabelItem key={label.title} shortcutKey={shortcutKeyByTitle.get(label.title)} isM4Enabled={true} hideCountTag={hideCountTagForLabel?.(label.title)} id={label.title} name={label.title} endEnhancer={labelListItemEnhancer?.(label.title)} isSelected={isSelected} isLast={isLast} annotationCount={annotationCountMap[label.title]} onClick={onLabelClick} onCountClick={onCountClick} handleLabelColorChange={handleLabelColorChange} color={label.style.strokeColor} renderIcon={getRenderIcon(label)} getListItemOverrides={getLabelListItemOverrides} getTagOverrides={getTagOverrides} />;
  };
  const isTaskQuestionnaireShown = !!(shouldShowTaskQuestionnaire && taskQuestionnaireComponent);
  const sectionHeaderClassName = css({
    ...theme.typography.LabelSmall,
    paddingBottom: theme.sizing.scale500
  });
  return <div className={css({
    paddingTop: theme.sizing.scale500,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale800,
    overflowY: 'auto'
  })}>
      {objectLabels.length > 0 && <div>
          <div className={sectionHeaderClassName}>Objects</div>
          {objectLabels.map((label, index) => renderLabelItem(label, index === objectLabels.length - 1))}
        </div>}
      {(nonObjectLabels.length > 0 || isTaskQuestionnaireShown) && <div>
          <div className={sectionHeaderClassName}>Non-Objects</div>
          {nonObjectLabels.map((label, index) => renderLabelItem(label, !isTaskQuestionnaireShown && index === nonObjectLabels.length - 1))}
          {isTaskQuestionnaireShown && <LabelItem key={TASK_QUESTIONNAIRE_ID} shortcutKey={taskQuestionnaireShortcutKey} isM4Enabled={true} hideCountTag={true} id={TASK_QUESTIONNAIRE_ID} name={TASK_QUESTIONNAIRE_TITLE} endEnhancer={taskQuestionnaireComponent.status !== 'OPTIONAL' ? <RequiredLabelBadge /> : null} isSelected={false} isLast={true} onClick={() => onOpenTaskQuestionnaire?.()} onCountClick={() => {}} handleLabelColorChange={() => false} color="transparent" renderIcon={() => <Document />} getListItemOverrides={getLabelListItemOverrides} getTagOverrides={getTagOverrides} />}
        </div>}
    </div>;
};
export default LabelList;